import { useEffect, useMemo, useState } from 'react';
import { checkFixturesPrinted } from '@/frontend/reports/printedChecks';
import { CheckResult, isRulesPath } from '@/reports/bundles/expectations';
import { CompiledReportBundle, REPORT_BUNDLE_FIXTURE_DIR, ReportBundleFiles } from '@/reports/bundles/types';

/**
 * The printed document's checks, made in this browser on the draft as last
 * saved — the same files and compile the server's results are of — and made
 * again whenever a save changes either. `pending` while they are being made.
 */
export function usePrintedChecks(compiled: CompiledReportBundle | null, saved: ReportBundleFiles | undefined): { byFile: Record<string, CheckResult[]>; pending: boolean } {
  // what the checks read: the compile, the fixtures and the rules files
  const relevant = useMemo(() => {
    const picked = Object.fromEntries(Object.entries(saved ?? {}).filter(([path]) => (path.startsWith(REPORT_BUNDLE_FIXTURE_DIR) && path.endsWith('.json')) || isRulesPath(path)));
    return { files: picked, key: `${compiled?.hash ?? ''}\n${JSON.stringify(picked)}` };
  }, [compiled?.hash, saved]);
  const holdsPrinted = (compiled?.fixtureResults ?? []).some((r) => (r.printed ?? 0) > 0);
  const [made, setMade] = useState<{ key: string; byFile: Record<string, CheckResult[]> }>({ key: '', byFile: {} });

  useEffect(() => {
    if (!compiled || !holdsPrinted) return;
    let live = true;
    // drawn on a tick of their own: a document is drawn synchronously, which React refuses inside an effect
    const timer = setTimeout(() => {
      void checkFixturesPrinted(compiled, relevant.files).then((byFile) => { if (live) setMade({ key: relevant.key, byFile }); });
    }, 0);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [relevant.key, holdsPrinted]);

  if (!compiled || !holdsPrinted) return { byFile: {}, pending: false };
  return made.key === relevant.key ? { byFile: made.byFile, pending: false } : { byFile: {}, pending: true };
}
